import React from 'react';
import { Avatar } from '../../../../../components';
import { DetailedAnalysis } from '../../../../../store/deals/deals.types';
import { companyLink } from './assets/index';
import { LinkediIcon } from './assets/LinkedinIcon';
import { TwitterIcon } from './assets/TwitterIcon';
import { FacebookIcon } from './assets/FacebookIcon';
import { ProspectDataSkeleton } from '../skeletons/ProspectDataSkeleton';
import { formatTotalFundingValue } from './utils/utils';

type Props = {
  detailedAnalysis: DetailedAnalysis | null;
  isLoading: boolean;
};

export const ProspectData: React.FC<Props> = ({
  detailedAnalysis,
  isLoading,
}): JSX.Element => {
  const row = (
    label: string,
    value: string | JSX.Element,
    domain?: string | null,
    isTotalFunding?: boolean
  ) => {
    const onCompanyLinkIconClick = (url: string) => {
      if (url.includes('.')) {
        window.open(`https://${url}`, '_blank');
      }
    };

    const conditionalLink = domain?.length ? (
      <img
        src={companyLink}
        alt=""
        className="prospect__body__row-value-company-link"
        onClick={() => onCompanyLinkIconClick(domain)}
      />
    ) : null;

    const valueClassName =
      value === 'N/A'
        ? 'no-data prospect__body__row-value'
        : 'prospect__body__row-value';

    const checkedLengthValue =
      value && typeof value === 'string' && value.length > 45
        ? `${value.slice(0, 45)}...`
        : value;

    const totalFundingValue = isTotalFunding
      ? formatTotalFundingValue(`${value}`)
      : null;

    const valueToShow = isTotalFunding ? totalFundingValue : checkedLengthValue;

    const conditionalValueSection =
      label === 'HQ' ? (
        <div className="prospect__body__row__hq">
          <span className="prospect__body__row__hq-tooltip">
            {checkedLengthValue}
          </span>
          <span
            className={
              value === 'N/A'
                ? 'no-data prospect__body__row-value'
                : 'prospect__body__row__hq-text'
            }
          >
            {checkedLengthValue}
          </span>
        </div>
      ) : (
        <span className={valueClassName}>
          {valueToShow}
          {conditionalLink}
        </span>
      );

    return (
      <div className="prospect__body__row">
        <span className="prospect__body__row-label">{label}</span>
        {conditionalValueSection}
      </div>
    );
  };

  const socialMediaRow = () => {
    const socialMedia =
      detailedAnalysis?.account?.metadata?.additionalData?.socialMedia;

    const linkedinObj = socialMedia?.find((el) => el.type === 'linkedin');

    const twitterObj = socialMedia?.find((el) => el.type === 'twitter');

    const facebookObj = socialMedia?.find((el) => el.type === 'facebook');

    const onIconClick = (url: string) => {
      if (url?.length && url.includes('http')) {
        window.open(`${url}`, '_blank');
      }
    };

    const rowToShow = (dataObj: typeof linkedinObj) => {
      const defineImgAndUrl = () => {
        let socialMediaIcon: React.FC | JSX.Element = <>No</>;

        let readyUrl = '';

        if (dataObj?.type === 'linkedin') {
          socialMediaIcon = <LinkediIcon />;
          readyUrl = `https://www.linkedin.com/${dataObj?.url}`;
        } else if (dataObj?.type === 'twitter') {
          socialMediaIcon = <TwitterIcon />;
          readyUrl = `https://twitter.com/${dataObj?.url}`;
        } else if (dataObj?.type === 'facebook') {
          socialMediaIcon = <FacebookIcon />;
          readyUrl = `https://www.facebook.com/${dataObj?.url}`;
        }

        return { socialMediaIcon, readyUrl };
      };

      const { socialMediaIcon, readyUrl } = defineImgAndUrl();

      return (
        <div
          className="prospect__body__row-link-img"
          onClick={() => onIconClick(readyUrl)}
        >
          {socialMediaIcon}
        </div>
      );
    };

    const result =
      linkedinObj || twitterObj || facebookObj ? (
        <div className="prospect__body__row">
          <span className="prospect__body__row-label">
            Company Social Media
          </span>
          <span className="prospect__body__row-value">
            {linkedinObj ? rowToShow(linkedinObj) : null}
            {twitterObj ? rowToShow(twitterObj) : null}
            {facebookObj ? rowToShow(facebookObj) : null}
          </span>
        </div>
      ) : null;

    return result;
  };

  const companyName = detailedAnalysis?.account?.metadata?.name || 'N/A';

  const headquarters =
    detailedAnalysis?.account?.metadata?.headquarters || 'N/A';

  const foundedYear = detailedAnalysis?.account?.metadata?.foundedYear || 'N/A';

  const category = detailedAnalysis?.account?.metadata?.category || 'N/A';

  const employees = detailedAnalysis?.account?.metadata?.employees || 'N/A';

  const domain = detailedAnalysis?.account?.metadata?.domain;

  const estimatedRevenue =
    detailedAnalysis?.account?.metadata?.additionalData?.estimatedAnnualRevenue;

  const totalFunding =
    detailedAnalysis?.account?.metadata?.additionalData?.totalFunding || 'N/A';

  const description =
    detailedAnalysis?.account?.metadata?.additionalData?.description;

  const attrs = {
    wrapper: {
      className: 'prospect',
    },

    prospect: {
      header: {
        className: 'prospect__header',
      },
      bodyWrapper: {
        className: 'prospect__body',
      },
    },

    divider: {
      className: 'prospect__body-divider',
    },

    contact: {
      bodyWrapper: {
        className: 'prospect__body__contact',
      },
      prospectWrapper: {
        className: 'prospect__body__contact__data',
      },
      avatarWrapper: {
        className: 'prospect__body__contact__data-avatar',
      },
      avatar: {
        style: { height: 48, width: 48 },
      },
      infoWrapper: {
        className: 'prospect__body__contact__data-info',
      },
      name: {
        className: 'prospect__body__contact__data-info-name',
      },
      otherInfo: {
        className: 'prospect__body__contact__data-info-other',
      },
      itemHasTooltip: {
        wrapper: {
          className: 'prospect__body__contact__data-info__has-tooltip',
        },
        itemText: {
          className: 'prospect__body__contact__data-info__has-tooltip-item',
        },
        tooltip: {
          className: 'prospect__body__contact__data-info__has-tooltip-tooltip',
        },
      },
      prospectsLinkedin: {
        className: 'prospect__body__contact__data-info-name-linkedin-img',
      },
    },
  };

  const companyInfo = (
    <>
      {row('Company', companyName, domain)}
      {row('HQ', `${headquarters}`)}
      {row('Year Founded', foundedYear)}
      {row('Category', category)}
      {row('Employees', employees)}
      {estimatedRevenue ? row('Est. Revenue', estimatedRevenue) : null}
      {totalFunding ? row('Total Funding', totalFunding, null, true) : null}
      {description ? row('Description', description) : null}
      {socialMediaRow()}
    </>
  );

  const onLinkedinIconClick = (url?: string) => {
    if (url?.length && url.includes('https://www.linkedin.com')) {
      window.open(`${url}`, '_blank');
    } else if (url?.length) {
      window.open(`https://www.linkedin.com/${url}`, '_blank');
    }
  };

  const participantsArr = detailedAnalysis?.prospectSideContacts;

  const participantsToShow =
    participantsArr && participantsArr.length
      ? participantsArr.map((el) => (
          <div key={el.email} {...attrs.contact.prospectWrapper}>
            <div {...attrs.contact.avatarWrapper}>
              <Avatar
                {...attrs.contact.avatar}
                image={el?.metadata?.avatarSrc || ''}
                userName={
                  el?.metadata?.avatarSrc?.length ? '' : el.metadata.name
                }
              />
            </div>
            <div {...attrs.contact.infoWrapper}>
              <div {...attrs.contact.name}>
                {el.metadata.name}{' '}
                {el?.metadata?.details?.socialMedia?.length ? (
                  <div
                    {...attrs.contact.prospectsLinkedin}
                    onClick={() =>
                      onLinkedinIconClick(
                        el?.metadata?.details?.socialMedia[0]?.url
                      )
                    }
                  >
                    <LinkediIcon />
                  </div>
                ) : null}
              </div>
              <div {...attrs.contact.otherInfo}>{el.email}</div>
              {el.metadata?.details ? (
                <>
                  {el.metadata?.details.title ? (
                    <div {...attrs.contact.itemHasTooltip.wrapper}>
                      <span {...attrs.contact.itemHasTooltip.tooltip}>
                        {el.metadata?.details.title}
                      </span>
                      <span {...attrs.contact.itemHasTooltip.itemText}>
                        {el.metadata?.details.title}
                      </span>
                    </div>
                  ) : null}
                  {el.metadata?.details.phone ? (
                    <div {...attrs.contact.otherInfo}>
                      {el.metadata?.details.phone}
                    </div>
                  ) : null}
                  {el.metadata?.details.timeZone ? (
                    <div {...attrs.contact.otherInfo}>
                      {el.metadata?.details.timeZone}
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>
        ))
      : null;

  return (
    <ProspectDataSkeleton isLoading={isLoading}>
      <div {...attrs.wrapper}>
        <div {...attrs.prospect.header}>Prospect Data</div>
        <div {...attrs.prospect.bodyWrapper}>
          {companyInfo}
          <div {...attrs.divider} />
          <div {...attrs.contact.bodyWrapper}>{participantsToShow}</div>
        </div>
      </div>
    </ProspectDataSkeleton>
  );
};
